import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import {
  fetchAssessmentForm,
  addAssessmentQuestion,
  updateAssessmentQuestion,
  deleteAssessmentQuestion,
  updateSurveyTemplate,
  archiveSurveyTemplate,
  createAssessmentFormat,
  createSurveyTemplate,
  activateSurveyTemplate,
  fetchAssessmentFormats,
  updateAssessmentFormat,
} from "../api/adminClient";
import { withDisplayNumbers, isFlatSurvey } from "../utils/questionNumbers";
import BulkQuestionUploadModal from "./BulkQuestionUploadModal";
import ScoringFormulaBuilder from "./ScoringFormulaBuilder";
import {
  QUESTION_TYPES,
  DEFAULT_SCORING_BY_TYPE,
  questionToImportType,
  optionsFromQuestion,
  typeNeedsOptions,
  emptyOptionRows,
} from "./questionTypeConfig";

function TypeSelect({ value, onChange, className = "" }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={
        className ||
        "border border-slate-200 rounded-lg px-2 py-2 text-xs bg-white shrink-0 max-w-[12rem]"
      }
      title="Question type"
    >
      {QUESTION_TYPES.map((t) => (
        <option key={t.value} value={t.value}>
          {t.label}
        </option>
      ))}
    </select>
  );
}

/** Label + weight rows for choice questions. */
function OptionWeightEditor({ rows, onChange }) {
  const list = rows?.length ? rows : emptyOptionRows();
  const update = (idx, patch) => {
    onChange(list.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Options &amp; weights
        </p>
        <button
          type="button"
          onClick={() =>
            onChange([...list, { label: "", weight: String(list.length + 1) }])
          }
          className="text-xs font-medium text-[#004F73] hover:underline"
        >
          + Add option
        </button>
      </div>
      <div className="grid grid-cols-[1fr_4.5rem_auto] gap-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-0.5">
        <span>Label</span>
        <span>Weight</span>
        <span />
      </div>
      {list.map((row, i) => (
        <div key={i} className="grid grid-cols-[1fr_4.5rem_auto] gap-2 items-center">
          <input
            type="text"
            value={row.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder={`Option ${i + 1}`}
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm bg-white"
          />
          <input
            type="number"
            value={row.weight}
            onChange={(e) => update(i, { weight: e.target.value })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white text-center"
            step="any"
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            disabled={list.length <= 2}
            className="text-xs text-slate-400 hover:text-red-600 px-1 disabled:opacity-30"
          >
            ✕
          </button>
        </div>
      ))}
      <p className="text-[11px] text-slate-400">
        Weight is the score used when this option is selected (e.g. Poor=1, Excellent=5).
      </p>
    </div>
  );
}

function rowsToPayload(rows) {
  const clean = (rows || [])
    .map((r) => ({
      label: String(r.label || "").trim(),
      weight: r.weight,
    }))
    .filter((r) => r.label);
  return {
    options: clean.map((r) => ({ label: r.label, weight: r.weight })),
    option_weights: clean.map((r) => {
      const n = Number(r.weight);
      return Number.isFinite(n) ? n : 1;
    }),
  };
}

/**
 * Create or edit an assessment: per-question types, scoring-by-type, bulk upload.
 */
export default function AssessmentEditorModal({
  open,
  mode = "create",
  templateId = "",
  templateMeta = null,
  systemFormatId = "",
  organizationId = "",
  onClose,
  onSaved,
}) {
  const isCreate = mode === "create";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [structure, setStructure] = useState("custom");
  const [scoringByType, setScoringByType] = useState(DEFAULT_SCORING_BY_TYPE);
  const [formatId, setFormatId] = useState("");
  const [sectionLabels, setSectionLabels] = useState(["Section 1", "Section 2"]);
  const [createAreaActive, setCreateAreaActive] = useState(false);
  const [steps, setSteps] = useState([]);
  const [surveyId, setSurveyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyQuestionId, setBusyQuestionId] = useState("");
  // Per-step add row: { text, type, options }
  const [draftByStep, setDraftByStep] = useState({});
  const [editingId, setEditingId] = useState("");
  const [editingText, setEditingText] = useState("");
  const [editingType, setEditingType] = useState("rating");
  const [editingOptionRows, setEditingOptionRows] = useState(emptyOptionRows());
  const [confirmDeleteAssessment, setConfirmDeleteAssessment] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showScoring, setShowScoring] = useState(false);

  const cleanSectionLabels = useMemo(
    () => sectionLabels.map((s) => s.trim()).filter(Boolean),
    [sectionLabels]
  );

  const flatMode = structure === "flat" || (!isCreate && isFlatSurvey(steps));

  const emptyDraft = () => ({
    text: "",
    type: "rating",
    optionRows: emptyOptionRows(),
  });

  useEffect(() => {
    if (!open) return;
    setConfirmDeleteAssessment(false);
    setEditingId("");
    setEditingText("");
    setEditingType("rating");
    setEditingOptionRows(emptyOptionRows());
    setDraftByStep({});
    setBusyQuestionId("");
    setBulkOpen(false);
    setScoringByType(DEFAULT_SCORING_BY_TYPE);
    setShowScoring(false);
    setFormatId("");

    if (isCreate) {
      setName("");
      setDescription("");
      setStructure("custom");
      setSectionLabels(["Section 1", "Section 2"]);
      setCreateAreaActive(false);
      setSteps([]);
      setSurveyId("");
      setLoading(false);
      return;
    }

    if (!templateId) return;
    setName(templateMeta?.name || "");
    setDescription("");
    setFormatId(templateMeta?.assessment_type_id || "");
    setLoading(true);

    const loadFormat = async (typeId) => {
      if (!typeId) return;
      try {
        const res = await fetchAssessmentFormats();
        const fmt = (res.data?.formats || []).find((f) => f.id === typeId);
        if (fmt?.scoring_by_type) {
          setScoringByType({ ...DEFAULT_SCORING_BY_TYPE, ...fmt.scoring_by_type });
        }
      } catch {
        /* ignore */
      }
    };

    Promise.all([
      fetchAssessmentForm({}, templateId),
      loadFormat(templateMeta?.assessment_type_id),
    ])
      .then(([res]) => {
        const full = withDisplayNumbers(res.data?.full_survey || []);
        setSteps(full);
        setSurveyId(res.data?.survey_id || templateId);
        setStructure(isFlatSurvey(full) ? "flat" : "custom");
        setSectionLabels(
          isFlatSurvey(full) ? [] : full.map((s) => s.title || `Section ${s.step}`)
        );
      })
      .catch((e) => {
        toast.error(e.message || "Could not load assessment");
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [open, isCreate, templateId, templateMeta?.name, templateMeta?.assessment_type_id, onClose]);

  useEffect(() => {
    if (!open || !isCreate) return;
    if (structure === "flat") {
      setSteps([{ step: 1, title: "", is_flat: true, questions: [], _drafts: [] }]);
      return;
    }
    if (structure === "custom") {
      setSteps((prev) =>
        cleanSectionLabels.map((label, i) => ({
          step: i + 1,
          title: label,
          questions: [],
          _drafts: prev[i]?._drafts || [],
        }))
      );
      return;
    }
    if (structure === "five_x") {
      setSteps([
        { step: 1, title: "Strategic Clarity", questions: [], _drafts: [] },
        { step: 2, title: "Scalable Growth", questions: [], _drafts: [] },
        { step: 3, title: "Relentless Focus", questions: [], _drafts: [] },
        { step: 4, title: "Disciplined Execution", questions: [], _drafts: [] },
        { step: 5, title: "Energized Culture", questions: [], _drafts: [] },
        { step: 6, title: "Financial Performance", questions: [], _drafts: [] },
      ]);
    }
  }, [open, isCreate, structure, cleanSectionLabels]);

  if (!open) return null;

  const questionCount = steps.reduce((n, s) => {
    return n + (s.questions || []).length + (s._drafts || []).length;
  }, 0);

  const canCreate =
    name.trim() &&
    (structure === "five_x"
      ? Boolean(systemFormatId)
      : structure === "flat"
        ? true
        : cleanSectionLabels.length > 0);

  const createBlocker = !isCreate
    ? ""
    : !name.trim()
      ? "Enter a name for this assessment."
      : structure === "five_x" && !systemFormatId
        ? "The 5X format is not available in this workspace yet."
        : structure === "custom" && cleanSectionLabels.length === 0
          ? "Name at least one section."
          : "";

  const numberedSteps = withDisplayNumbers(
    steps.map((s) => ({
      ...s,
      questions: [
        ...(s.questions || []).map((q) => ({
          ...q,
          question_type: questionToImportType(q),
        })),
        ...(s._drafts || []).map((d, i) => ({
          id: `draft-${s.step}-${i}`,
          text: d.text,
          question_type: d.type || "rating",
          _optionRows: d.optionRows || emptyOptionRows(),
          _isDraft: true,
          _draftIndex: i,
          _step: s.step,
        })),
      ],
    }))
  );

  const getDraft = (stepNum) => draftByStep[stepNum] || emptyDraft();

  const setDraftField = (stepNum, patch) => {
    setDraftByStep((d) => ({
      ...d,
      [stepNum]: { ...emptyDraft(), ...(d[stepNum] || {}), ...patch },
    }));
  };

  const addDraftQuestion = (stepNum) => {
    const draft = getDraft(stepNum);
    const text = (draft.text || "").trim();
    if (!text) return;
    const qType = draft.type || "rating";
    const payload = rowsToPayload(draft.optionRows);
    if (typeNeedsOptions(qType) && payload.options.length < 2) {
      toast.error("Choice questions need at least 2 options with labels");
      return;
    }

    if (isCreate) {
      setSteps((rows) =>
        rows.map((s) =>
          s.step === stepNum
            ? {
                ...s,
                _drafts: [
                  ...(s._drafts || []),
                  {
                    text,
                    type: qType,
                    optionRows: draft.optionRows || emptyOptionRows(),
                  },
                ],
              }
            : s
        )
      );
      setDraftByStep((d) => ({ ...d, [stepNum]: emptyDraft() }));
      return;
    }

    if (!surveyId) return;
    setBusyQuestionId(`add-${stepNum}`);
    addAssessmentQuestion({
      survey_id: surveyId,
      step: stepNum,
      question_text: text,
      question_type: qType,
      options: payload.options,
      option_weights: payload.option_weights,
    })
      .then(async () => {
        toast.success("Question added");
        setDraftByStep((d) => ({ ...d, [stepNum]: emptyDraft() }));
        const res = await fetchAssessmentForm({}, templateId);
        setSteps(withDisplayNumbers(res.data?.full_survey || []));
        setSurveyId(res.data?.survey_id || surveyId);
        onSaved?.({ id: templateId, soft: true });
      })
      .catch((e) => toast.error(e.message || "Could not add question"))
      .finally(() => setBusyQuestionId(""));
  };

  const removeDraft = (stepNum, idx) => {
    setSteps((rows) =>
      rows.map((s) =>
        s.step === stepNum
          ? { ...s, _drafts: (s._drafts || []).filter((_, i) => i !== idx) }
          : s
      )
    );
  };

  const startEditQuestion = (q) => {
    if (q._isDraft) return;
    setEditingId(q.id);
    setEditingText(q.source_text || q.text || "");
    setEditingType(questionToImportType(q));
    const rows = optionsFromQuestion(q);
    setEditingOptionRows(rows.length ? rows : emptyOptionRows());
  };

  const saveEditQuestion = async () => {
    if (!surveyId || !editingId || !editingText.trim()) return;
    const payload = rowsToPayload(editingOptionRows);
    if (typeNeedsOptions(editingType) && payload.options.length < 2) {
      toast.error("Choice questions need at least 2 options with labels");
      return;
    }
    setBusyQuestionId(editingId);
    try {
      await updateAssessmentQuestion({
        survey_id: surveyId,
        template: "full",
        question_id: editingId,
        new_text: editingText.trim(),
        question_type: editingType,
        options: typeNeedsOptions(editingType) ? payload.options : [],
        option_weights: typeNeedsOptions(editingType) ? payload.option_weights : [],
      });
      toast.success("Question updated");
      setEditingId("");
      const res = await fetchAssessmentForm({}, templateId);
      setSteps(withDisplayNumbers(res.data?.full_survey || []));
      onSaved?.({ id: templateId, soft: true });
    } catch (e) {
      toast.error(e.message || "Could not update question");
    } finally {
      setBusyQuestionId("");
    }
  };

  const removeQuestion = async (q) => {
    if (q._isDraft) {
      removeDraft(q._step, q._draftIndex);
      return;
    }
    if (!surveyId || !q?.id) return;
    if (!window.confirm("Delete this question?")) return;
    setBusyQuestionId(q.id);
    try {
      await deleteAssessmentQuestion(surveyId, q.id);
      toast.success("Question deleted");
      const res = await fetchAssessmentForm({}, templateId);
      setSteps(withDisplayNumbers(res.data?.full_survey || []));
      onSaved?.({ id: templateId, soft: true });
    } catch (e) {
      toast.error(e.message || "Could not delete question");
    } finally {
      setBusyQuestionId("");
    }
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setSaving(true);
    try {
      let assessmentTypeId = structure === "five_x" ? systemFormatId : undefined;
      if (structure === "custom" || structure === "flat") {
        const ratingFormula =
          scoringByType?.importance_agreement?.formula || "importance_x_agreement";
        const fmtRes = await createAssessmentFormat({
          name: name.trim(),
          description: description.trim(),
          layout: structure === "flat" ? "flat" : "sectioned",
          scorer_id: ratingFormula,
          scoring_by_type: scoringByType,
          sections:
            structure === "flat"
              ? []
              : cleanSectionLabels.map((label) => ({
                  label,
                  include_in_default_lite: true,
                })),
        });
        assessmentTypeId = fmtRes.data?.format?.id || fmtRes.data?.id;
        if (!assessmentTypeId) throw new Error("Format was created but no id returned");
      }

      const res = await createSurveyTemplate({
        name: name.trim(),
        assessment_type_id: assessmentTypeId,
        ...(organizationId ? { organization_id: organizationId } : {}),
      });
      const newId = res.data?.id;
      if (!newId) throw new Error("Assessment created but no id returned");

      for (const s of steps) {
        for (const draft of s._drafts || []) {
          const payload = rowsToPayload(draft.optionRows);
          await addAssessmentQuestion({
            survey_id: newId,
            step: s.step,
            question_text: draft.text,
            question_type: draft.type || "rating",
            options: payload.options,
            option_weights: payload.option_weights,
          });
        }
      }

      toast.success(`Assessment "${name.trim()}" created`);
      onSaved?.({ id: newId });
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Could not create assessment");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveScoring = async () => {
    if (!formatId) {
      toast.error("This assessment has no custom format to update scoring on");
      return;
    }
    setSaving(true);
    try {
      await updateAssessmentFormat(formatId, {
        scoring_by_type: scoringByType,
        scorer_id: scoringByType?.importance_agreement?.formula,
      });
      toast.success("Scoring formulas saved");
      onSaved?.({ id: templateId, soft: true });
    } catch (e) {
      toast.error(e.message || "Could not save scoring");
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async () => {
    if (!templateId || !name.trim()) return;
    if (name.trim() === (templateMeta?.name || "")) return;
    setSaving(true);
    try {
      await updateSurveyTemplate(templateId, { name: name.trim() });
      toast.success("Name updated");
      onSaved?.({ id: templateId, soft: true });
    } catch (e) {
      toast.error(e.message || "Could not rename");
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!templateId || templateMeta?.inherited) return;
    const count = questionCount || templateMeta?.question_count || 0;
    if (count === 0) {
      toast.error("Add at least one question before activating this assessment.");
      return;
    }
    setSaving(true);
    try {
      await activateSurveyTemplate(templateId);
      toast.success("Assessment set as active");
      onSaved?.({ id: templateId, soft: true });
    } catch (e) {
      toast.error(e.message || "Could not activate");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssessment = async () => {
    if (!templateId) return;
    setSaving(true);
    try {
      await archiveSurveyTemplate(templateId);
      toast.success("Assessment deleted");
      onSaved?.({ id: "", deleted: true });
      onClose?.();
    } catch (e) {
      toast.error(e.message || "Could not delete assessment");
    } finally {
      setSaving(false);
      setConfirmDeleteAssessment(false);
    }
  };

  const reloadAfterBulk = async (data) => {
    const id = data?.id || templateId;
    if (!id) return;
    const res = await fetchAssessmentForm({}, id);
    setSteps(withDisplayNumbers(res.data?.full_survey || []));
    setSurveyId(res.data?.survey_id || id);
    onSaved?.({ id, soft: true });
  };

  const typeLabel = (type) =>
    QUESTION_TYPES.find((t) => t.value === type)?.label || type;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assessment-editor-title"
      >
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
            <div>
              <h3
                id="assessment-editor-title"
                className="text-lg font-semibold text-slate-900"
              >
                {isCreate ? "New assessment" : "Edit assessment"}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {questionCount} question{questionCount !== 1 ? "s" : ""}
                {flatMode ? " · Flat" : ""}
                {!isCreate && templateMeta?.is_active ? " · Active" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isCreate && surveyId ? (
                <button
                  type="button"
                  onClick={() => setBulkOpen(true)}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] hover:bg-[#004F73]/5"
                >
                  Bulk upload
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none w-8 h-8"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>

          <div className="px-5 py-4 space-y-5 overflow-y-auto flex-1">
            {loading ? (
              <p className="text-sm text-[#004F73] py-8 text-center">Loading…</p>
            ) : (
              <>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        autoFocus={isCreate}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Leadership Assessment 2026"
                        className={`flex-1 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                          isCreate && createAreaActive && !name.trim()
                            ? "border border-red-400 focus:ring-red-200"
                            : "border border-slate-200 focus:ring-[#004f73]/25"
                        }`}
                      />
                      {!isCreate && (
                        <button
                          type="button"
                          onClick={handleRename}
                          disabled={
                            saving ||
                            !name.trim() ||
                            name.trim() === (templateMeta?.name || "")
                          }
                          className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          Save name
                        </button>
                      )}
                    </div>
                  </div>

                  {isCreate && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Optional"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm"
                        />
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">Structure</p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                            <input
                              type="radio"
                              name="structure"
                              checked={structure === "flat"}
                              onChange={() => setStructure("flat")}
                              className="mt-1"
                            />
                            <span>
                              <span className="block text-sm font-medium text-slate-800">
                                No sections (flat list)
                              </span>
                              <span className="block text-xs text-slate-500 mt-0.5">
                                Numbers 1, 2, 3…
                              </span>
                            </span>
                          </label>
                          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                            <input
                              type="radio"
                              name="structure"
                              checked={structure === "custom"}
                              onChange={() => setStructure("custom")}
                              className="mt-1"
                            />
                            <span>
                              <span className="block text-sm font-medium text-slate-800">
                                Custom sections
                              </span>
                              <span className="block text-xs text-slate-500 mt-0.5">
                                Numbers 1.1, 1.2, 2.1…
                              </span>
                            </span>
                          </label>
                          {systemFormatId ? (
                            <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
                              <input
                                type="radio"
                                name="structure"
                                checked={structure === "five_x"}
                                onChange={() => setStructure("five_x")}
                                className="mt-1"
                              />
                              <span>
                                <span className="block text-sm font-medium text-slate-800">
                                  5X classic (6 pillars)
                                </span>
                              </span>
                            </label>
                          ) : null}
                        </div>
                      </div>

                      {structure === "custom" && (
                        <div className="space-y-2 rounded-xl bg-slate-50 border border-slate-100 p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Sections
                          </p>
                          {sectionLabels.map((label, i) => (
                            <div key={i} className="flex gap-2">
                              <input
                                type="text"
                                value={label}
                                onChange={(e) =>
                                  setSectionLabels((rows) =>
                                    rows.map((r, idx) => (idx === i ? e.target.value : r))
                                  )
                                }
                                placeholder={`Section ${i + 1}`}
                                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setSectionLabels((rows) =>
                                    rows.length <= 1
                                      ? rows
                                      : rows.filter((_, idx) => idx !== i)
                                  )
                                }
                                className="text-xs text-slate-500 hover:text-red-600 px-2"
                                disabled={sectionLabels.length <= 1}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => setSectionLabels((rows) => [...rows, ""])}
                            className="text-sm font-medium text-[#004f73] hover:underline"
                          >
                            + Add section
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {(isCreate
                    ? structure === "custom" || structure === "flat"
                    : Boolean(formatId)) && (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setShowScoring((v) => !v)}
                        className="text-sm font-semibold text-[#004F73] hover:underline"
                      >
                        {showScoring ? "Hide" : "Show"} scoring formula builder
                      </button>
                      {showScoring && (
                        <>
                          <ScoringFormulaBuilder
                            value={scoringByType}
                            onChange={setScoringByType}
                          />
                          {!isCreate && formatId ? (
                            <button
                              type="button"
                              onClick={handleSaveScoring}
                              disabled={saving}
                              className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] hover:bg-[#004F73]/5 disabled:opacity-50"
                            >
                              Save scoring formulas
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {numberedSteps.length > 0 && (
                  <div className="space-y-4">
                    <p className="text-sm font-semibold text-slate-800">
                      Questions
                      <span className="ml-2 font-normal text-xs text-slate-400">
                        Type is chosen per question (same types as bulk upload)
                      </span>
                    </p>

                    {numberedSteps.map((s, stepIndex) => {
                      const stepNum = s.step ?? stepIndex + 1;
                      const qs = s.questions || [];
                      const draft = getDraft(stepNum);
                      return (
                        <div
                          key={stepNum || s.title || "flat"}
                          className="rounded-xl border border-slate-200 overflow-hidden"
                        >
                          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                            <p className="text-sm font-semibold text-[#004F73]">
                              {flatMode
                                ? "All questions"
                                : s.title || `Section ${stepNum}`}
                            </p>
                          </div>
                          <ul className="divide-y divide-slate-100">
                            {qs.map((q) => (
                              <li
                                key={q.id}
                                className={`px-3 py-2.5 ${
                                  q._isDraft ? "bg-amber-50/40" : ""
                                }`}
                              >
                                {editingId === q.id ? (
                                  <div className="space-y-2">
                                    <div className="flex gap-2 items-start">
                                      <span className="shrink-0 mt-1.5 w-9 text-center text-xs font-bold text-[#004F73] bg-[#004F73]/10 rounded-md py-1">
                                        {q.display_number}
                                      </span>
                                      <div className="flex-1 space-y-2">
                                        <TypeSelect
                                          value={editingType}
                                          onChange={setEditingType}
                                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                                        />
                                        <textarea
                                          value={editingText}
                                          onChange={(e) => setEditingText(e.target.value)}
                                          rows={2}
                                          className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm"
                                        />
                                        {typeNeedsOptions(editingType) && (
                                          <OptionWeightEditor
                                            rows={editingOptionRows}
                                            onChange={setEditingOptionRows}
                                          />
                                        )}
                                        <div className="flex gap-2">
                                          <button
                                            type="button"
                                            onClick={saveEditQuestion}
                                            disabled={
                                              busyQuestionId === q.id || !editingText.trim()
                                            }
                                            className="text-xs font-semibold px-2.5 py-1 rounded-md bg-[#004F73] text-white disabled:opacity-50"
                                          >
                                            Save
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => setEditingId("")}
                                            className="text-xs px-2.5 py-1 rounded-md border border-slate-200"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex gap-2 items-start">
                                    <span className="shrink-0 mt-0.5 w-9 text-center text-xs font-bold text-[#004F73] bg-[#004F73]/10 rounded-md py-1">
                                      {q.display_number}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-slate-700 leading-snug">
                                        {q.source_text || q.text}
                                      </p>
                                      <p className="text-[11px] text-slate-400 mt-0.5">
                                        {typeLabel(q.question_type)}
                                        {q._isDraft ? " · Draft" : ""}
                                      </p>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                      {!q._isDraft && !isCreate && (
                                        <button
                                          type="button"
                                          onClick={() => startEditQuestion(q)}
                                          className="text-xs font-medium text-[#004F73] hover:underline px-1"
                                        >
                                          Edit
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => removeQuestion(q)}
                                        disabled={busyQuestionId === q.id}
                                        className="text-xs font-medium text-red-500 hover:underline px-1 disabled:opacity-50"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            ))}
                            {qs.length === 0 && (
                              <li className="px-3 py-3 text-xs text-slate-400">
                                No questions yet — pick a type for each question below.
                              </li>
                            )}
                          </ul>

                          {/* Per-question add row: type is for THIS question only */}
                          <div className="px-3 py-2.5 border-t border-slate-100 space-y-2 bg-white">
                            <div className="flex flex-wrap gap-2 items-center">
                              <TypeSelect
                                value={draft.type || "rating"}
                                onChange={(type) => setDraftField(stepNum, { type })}
                              />
                              <input
                                type="text"
                                value={draft.text || ""}
                                onChange={(e) =>
                                  setDraftField(stepNum, { text: e.target.value })
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !typeNeedsOptions(draft.type)) {
                                    e.preventDefault();
                                    addDraftQuestion(stepNum);
                                  }
                                }}
                                placeholder="Question text…"
                                className="flex-1 min-w-[12rem] border border-slate-200 rounded-lg px-3 py-2 text-sm"
                              />
                              <button
                                type="button"
                                onClick={() => addDraftQuestion(stepNum)}
                                disabled={
                                  !(draft.text || "").trim() ||
                                  busyQuestionId === `add-${stepNum}`
                                }
                                className="px-3 py-2 text-sm font-semibold rounded-lg text-white bg-[#004F73] hover:opacity-90 disabled:opacity-40 shrink-0"
                              >
                                {busyQuestionId === `add-${stepNum}` ? "…" : "Add"}
                              </button>
                            </div>
                            {typeNeedsOptions(draft.type) && (
                              <OptionWeightEditor
                                rows={draft.optionRows || emptyOptionRows()}
                                onChange={(optionRows) =>
                                  setDraftField(stepNum, { optionRows })
                                }
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!isCreate && !templateMeta?.is_baseline && (
                  <div className="rounded-xl border border-red-100 bg-red-50/40 p-3 space-y-2">
                    <p className="text-sm font-semibold text-slate-800">Danger zone</p>
                    {!confirmDeleteAssessment ? (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteAssessment(true)}
                        className="text-sm font-medium text-red-600 hover:underline"
                      >
                        Delete this assessment…
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleDeleteAssessment}
                          disabled={saving}
                          className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-red-600 text-white disabled:opacity-50"
                        >
                          Confirm delete
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteAssessment(false)}
                          className="px-3 py-1.5 text-sm rounded-lg border border-slate-200"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-2 px-5 py-4 border-t border-slate-100 shrink-0">
            <div className="flex gap-2">
              {!isCreate && !templateMeta?.is_active && !templateMeta?.inherited && (
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  Set active
                </button>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 ml-auto">
              {isCreate && !canCreate && createBlocker ? (
                <p className="text-xs text-amber-700">{createBlocker}</p>
              ) : null}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  {isCreate ? "Cancel" : "Close"}
                </button>
                {isCreate && (
                  <button
                    type="button"
                    disabled={saving || !canCreate}
                    onClick={handleCreate}
                    onMouseEnter={() => setCreateAreaActive(true)}
                    onFocus={() => setCreateAreaActive(true)}
                    onBlur={() => setCreateAreaActive(false)}
                    className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-[#004f73] hover:opacity-90 disabled:opacity-50"
                  >
                    {saving ? "Creating…" : "Create assessment"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BulkQuestionUploadModal
        open={bulkOpen}
        surveyId={surveyId}
        templateName={name || templateMeta?.name || ""}
        organizationId={organizationId}
        overlayClassName="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4"
        onClose={() => setBulkOpen(false)}
        onApplied={reloadAfterBulk}
      />
    </>,
    document.body
  );
}
